import type { Reference } from "@hali-shares/deck-schema";
import { type ReactNode, createContext, useContext, useEffect, useId, useState } from "react";

type Base = { label: string; hint?: string; max?: number };

function Label({ label, htmlFor, count, max }: { label: string; htmlFor: string; count?: number; max?: number }) {
  return (
    // The counter sits beside the <label>, not in it, so the field's name is just its label.
    <div className="field-label">
      <label htmlFor={htmlFor}>{label}</label>
      {max !== undefined && count !== undefined && (
        <span className="field-count" data-over={count > max} aria-live="polite">
          {count}/{max}
        </span>
      )}
    </div>
  );
}

export function TextField({
  label,
  hint,
  max,
  value,
  onChange,
  mono,
  placeholder,
}: Base & {
  value: string | undefined;
  onChange: (v: string) => void;
  mono?: boolean;
  placeholder?: string;
}) {
  const id = useId();
  return (
    <div className="field">
      <Label label={label} htmlFor={id} count={(value ?? "").length} max={max} />
      <input
        id={id}
        className={`input${mono ? " input-mono" : ""}`}
        value={value ?? ""}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

export function TextArea({
  label,
  hint,
  max,
  value,
  onChange,
  mono,
  rows = 3,
}: Base & {
  value: string | undefined;
  onChange: (v: string) => void;
  mono?: boolean;
  rows?: number;
}) {
  const id = useId();
  return (
    <div className="field">
      <Label label={label} htmlFor={id} count={(value ?? "").length} max={max} />
      <textarea
        id={id}
        rows={rows}
        className={`textarea${mono ? " textarea-mono" : ""}`}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
      />
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

/** One item per line. Keeps the raw text while typing so blank lines don't vanish under the cursor. */
export function LinesField({
  label,
  hint,
  value,
  onChange,
  maxItems,
}: Omit<Base, "max"> & {
  value: string[];
  onChange: (v: string[]) => void;
  maxItems?: number;
}) {
  const id = useId();
  const [text, setText] = useState(value.join("\n"));
  const items = text
    .split("\n")
    .map((l) => l.trim())
    .filter(Boolean);
  return (
    <div className="field">
      <Label label={label} htmlFor={id} count={items.length} max={maxItems} />
      <textarea
        id={id}
        rows={Math.max(3, items.length + 1)}
        className="textarea"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onChange(
            e.target.value
              .split("\n")
              .map((l) => l.trim())
              .filter(Boolean),
          );
        }}
      />
      {hint && <span className="field-hint">{hint}</span>}
    </div>
  );
}

export function NumberField({
  label,
  value,
  onChange,
  min,
  max,
  step,
  optional,
}: {
  label: string;
  value: number | undefined;
  onChange: (v: number | undefined) => void;
  min?: number;
  max?: number;
  step?: number;
  optional?: boolean;
}) {
  const id = useId();
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="input input-mono"
        type="number"
        min={min}
        max={max}
        step={step}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value === "" && optional ? undefined : Number(e.target.value))}
      />
    </div>
  );
}

export function Select<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  const id = useId();
  return (
    <div className="field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <select id={id} className="select" value={value} onChange={(e) => onChange(e.target.value as T)}>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

export function Check({
  label,
  checked,
  onChange,
}: {
  label: string;
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="check">
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      {label}
    </label>
  );
}

/** Lets a footnote picker add a reference to the deck from a pasted link. Provided by the block drawer. */
export type AddReference = (url: string) => Promise<{ id: string; ok: boolean } | { error: string }>;
export const AddReferenceContext = createContext<AddReference | null>(null);

/**
 * Footnotes for a block: pick from this deck's reference list, or paste a link to add a new one
 * (title, authors and year are read from the link). Nothing here is fixed; it lists whatever the deck has.
 */
export function RefsPicker({
  references,
  value,
  onChange,
  label = "Footnotes",
}: {
  references: Reference[];
  value: string[];
  onChange: (v: string[]) => void;
  label?: string;
}) {
  const add = useContext(AddReferenceContext);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  const id = useId();

  const submit = async () => {
    if (!add || !url.trim()) return;
    setBusy(true);
    setNote(null);
    const r = await add(url.trim());
    setBusy(false);
    if ("error" in r) return setNote(r.error);
    if (!value.includes(r.id)) onChange([...value, r.id]);
    setUrl("");
    setNote(r.ok ? null : "Added, but the link couldn’t be read: set its title with “+ Reference”.");
  };

  return (
    <div className="field">
      <span className="field-label">
        <span>{label}</span>
        <span className="field-count">{references.length} in this deck</span>
      </span>
      {references.length === 0 ? (
        <p className="empty">This deck has no references yet{add ? ". Paste a link below to add one." : "."}</p>
      ) : (
        <div className="refs-pick">
          {references.map((r) => (
            <Check
              key={r.id}
              label={`${r.title}${r.authors ? ` · ${r.authors}` : ""}`}
              checked={value.includes(r.id)}
              onChange={(on) => onChange(on ? [...value, r.id] : value.filter((x) => x !== r.id))}
            />
          ))}
        </div>
      )}
      {add && (
        <div className="row-gap" style={{ flexWrap: "nowrap" }}>
          <input
            id={id}
            className="input input-mono"
            aria-label="Add a reference by link"
            placeholder="Paste a link to add a reference"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                void submit();
              }
            }}
          />
          <button type="button" className="s-btn" disabled={busy || !url.trim()} onClick={submit}>
            {busy ? "Adding…" : "Add"}
          </button>
        </div>
      )}
      {note && <span className="field-hint">{note}</span>}
    </div>
  );
}

export function Group({
  title,
  id,
  actions,
  children,
}: {
  title: string;
  id?: string;
  actions?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="group">
      <div className="group-head">
        <span className="group-title">{title}</span>
        {id && <span className="group-id">{id}</span>}
        {actions}
      </div>
      {children}
    </div>
  );
}

/** A destructive button that needs a second click within 4 seconds. */
export function ConfirmButton({
  label,
  confirmLabel,
  onConfirm,
  className = "s-btn s-btn-danger",
  disabled,
}: {
  label: string;
  confirmLabel: string;
  onConfirm: () => void;
  className?: string;
  disabled?: boolean;
}) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = window.setTimeout(() => setArmed(false), 4000);
    return () => window.clearTimeout(t);
  }, [armed]);
  return (
    <button
      type="button"
      className={className}
      data-confirm={armed}
      disabled={disabled}
      onClick={() => {
        if (!armed) return setArmed(true);
        setArmed(false);
        onConfirm();
      }}
    >
      {armed ? confirmLabel : label}
    </button>
  );
}

export function Issues({ issues }: { issues: { path: string; message: string }[] }) {
  if (!issues.length) return null;
  return (
    <div className="banner banner-yellow" role="alert">
      This won’t save yet:
      <ul className="issues">
        {issues.map((i, n) => (
          <li key={n}>
            {i.path ? `${i.path}: ` : ""}
            {i.message}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function UpIcon({ down }: { down?: boolean }) {
  return (
    <svg
      aria-hidden="true"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {down ? <path d="M3 4.5 L6 7.5 L9 4.5" /> : <path d="M3 7.5 L6 4.5 L9 7.5" />}
    </svg>
  );
}

export function XIcon() {
  return (
    <svg
      aria-hidden="true"
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    >
      <path d="M3 3 L9 9 M9 3 L3 9" />
    </svg>
  );
}

export function move<T>(list: T[], from: number, to: number): T[] {
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  const [item] = next.splice(from, 1);
  next.splice(to, 0, item!);
  return next;
}
